import React from 'react';

import * as styles from './AutoplayProgressBar.styles';

export interface AutoplayProgressBarProps {
  /** Bump to restart the bar from empty — e.g. the active slide index. */
  cycleKey: number;
  interval: number;
  /** Called when the bar fills, to advance the carousel. */
  onComplete: () => void;
  paused: boolean;
  /**
   * Fades the bar out while the carousel slides, so it is not visible resetting mid-move, and holds
   * the countdown until it is back on show.
   */
  transitioning: boolean;
}

/**
 * The bar along the bottom of the current image showing how long until autoplay advances — and which
 * doubles as the autoplay timer itself.
 *
 * The fill is a CSS keyframe animation lasting `interval` ms. Because it *is* the timer, pausing
 * comes for free: `animation-play-state: paused` freezes the bar exactly where it is and resumes from
 * the same point, so hovering or focusing holds the countdown rather than restarting it. When the
 * animation finishes we advance via `onComplete`; the resulting slide change bumps `cycleKey`,
 * remounting the fill so the next countdown starts from empty.
 *
 * Being the clock has one consequence worth knowing: a `display: none` subtree suspends CSS
 * animations, so this must never be hidden that way or autoplay stops. It shows at every screen size,
 * which is what makes that safe, and `transitioning` fades it out with opacity for the same reason.
 *
 * `transitioning` also holds the countdown, using the same freeze as `paused`. The slide change has
 * already restarted the fill from empty, so without this the bar would spend the move counting down
 * out of sight and reappear part-filled. Held instead, it starts from empty the moment it is back on
 * show, and the interval measures the time the slide is actually up.
 */
export const AutoplayProgressBar = ({
  cycleKey,
  interval,
  onComplete,
  paused,
  transitioning,
}: AutoplayProgressBarProps) => (
  <div
    aria-hidden="true"
    css={styles.track}
    data-testid="autoplay-progress"
    data-transitioning={transitioning}
  >
    <div
      css={styles.progress}
      data-autoplay-progress
      key={cycleKey}
      onAnimationEnd={onComplete}
      style={{
        animationDuration: `${interval}ms`,
        animationPlayState: paused || transitioning ? 'paused' : 'running',
      }}
    />
  </div>
);
